import { Box, Typography } from "@mui/material";
import { isNil } from "lodash-es";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AppIconButton from "@/common/components/Button/AppIconButton";
import Datetime from "@/common/components/Datetime/Datetime";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import InlineApiEnumValueList from "@/common/components/Enum/InlineApiEnumValueList";
import FileListView from "@/common/components/Files/FileListView";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import GeneralStageHistory from "@/common/components/GeneralStageHistory";
import AppIcon from "@/common/components/Icons/AppIcon";
import BaseEditExternalNumberModal from "@/common/components/Modals/common/BaseEditExternalNumberModal";
import BaseEditMileageModal from "@/common/components/Modals/common/BaseEditMileageModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import GeneralPriceSummaryDisplay from "@/common/components/PriceSummary/GeneralPriceSummaryDisplay";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ViewVariant } from "@/common/ts/layout";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, EntityType, WashDto } from "@/core/api/generated/v1";

import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import GeneralByWhoDisplay from "../../../General/Display/GeneralByWhoDisplay";
import SupplierLink from "../../../Supplier/SupplierLink";
import VehicleLink from "../../../Vehicle/VehicleLink";

interface Props {
  wash: WashDto;
  onUpdate: (newValue?: WashDto) => void;
}

export default function OverviewTabContent({ wash, onUpdate }: Props) {
  const { enqueueSnackbar } = useAppSnackbar();
  const { hasPermissions } = useAuthorizationInfo();

  const [isUpdateMileageModalOpened, setIsUpdateMileageModalOpened] = useState(false);
  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader title={"Details"} subtitle2={<EntityChangedByInfoCard entity={wash} />} />
        }
      >
        <ViewContentBlock>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            {/* <InfoTile
              label='Number'
              iconOf='localNumber'
              isEmpty={isNil(wash?.localNumber)}
            >
              {wash?.localNumber}
            </InfoTile> */}

            <InfoTile label='Vehicle' iconOf='vehicle' isEmpty={isNil(wash?.vehicle)}>
              <VehicleLink entity={wash?.vehicle} />
            </InfoTile>

            {/* <InfoTile label='Contract' iconOf='contract' isEmpty={isNil(wash?.contract)}>
              <ContractLink entity={wash?.contract} />
            </InfoTile> */}

            <InfoTile label='Supplier' iconOf='supplier' isEmpty={isNil(wash?.supplier)}>
              <SupplierLink entity={wash?.supplier} />
            </InfoTile>

            <InfoTile
              label='External number'
              iconOf='externalNumber'
              isEmpty={!wash.externalNumber}
              endAdornment={
                <BaseEditExternalNumberModal
                  initialValues={{ externalNumber: wash.externalNumber }}
                  onConfirm={async (externalNumber) => {
                    await apiClient.washesApi.apiV1WashesWashIdExternalNumberPut({
                      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                      washId: wash.id || "",
                      generalExternalNumberInputDto: { externalNumber },
                    });
                    onUpdate();
                  }}
                />
              }
            >
              {wash.externalNumber}
            </InfoTile>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile label='Type' iconOf='washType' isEmpty={isNil((wash?.types || [])[0])}>
              <InlineApiEnumValueList type='WashType' values={wash.types} />
            </InfoTile>

            <InfoTile
              label='Schedule type'
              iconOf='washScheduleType'
              isEmpty={isNil(wash?.scheduleType)}
            >
              <InlineApiEnumValue type='WashScheduleType' value={wash.scheduleType} />
            </InfoTile>

            <InfoTile label='Scheduled At' iconOf='date' isEmpty={isNil(wash?.scheduledAt)}>
              <Datetime datetime={wash?.scheduledAt} />
            </InfoTile>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile
              label='Mileage'
              iconOf='gauge'
              isEmpty={!wash.mileage}
              endAdornment={
                <BaseEditMileageModal
                  vehicleId={wash.vehicle?.id}
                  mileage={wash.mileage}
                  trigger={
                    <AppIconButton
                      sx={{ p: "2px", ml: 0.5 }}
                      onClick={() => setIsUpdateMileageModalOpened(true)}
                    >
                      <AppIcon of='edit' sx={{ width: "0.8em", height: "0.8em" }} />
                    </AppIconButton>
                  }
                  triggerParams={{ isDisableDefaultAction: true }}
                  open={isUpdateMileageModalOpened}
                  onClose={() => setIsUpdateMileageModalOpened(false)}
                  onConfirm={async (mileage) => {
                    await apiClient.washesApi.apiV1WashesWashIdMileagePut({
                      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                      washId: wash.id || "",
                      updateVehicleMileageDto: {
                        vehicleId: wash.vehicle?.id,
                        mileage: mileage,
                      },
                    });
                  }}
                  onMileageChanged={() => onUpdate()}
                />
              }
            >
              {wash.mileage?.mileage} {wash.mileage?.unit}
            </InfoTile>

            <InfoTile label='Fuel level' iconOf='fuel' isEmpty={isNil(wash?.fuelLevel)}>
              {wash?.fuelLevel}
            </InfoTile>

            <InfoTile
              label='Responsible user'
              iconOf='inspector'
              isEmpty={isNil(wash?.responsibleUser)}
            >
              <GeneralByWhoDisplay who={wash.responsibleUser} />
            </InfoTile>

            <InfoTile
              label='Notes'
              iconOf='notes'
              isEmpty={!wash.notes}
              endAdornment={
                <BaseEditNotesModal
                  initialValues={{ notes: wash.notes }}
                  onConfirm={async (notes) => {
                    await apiClient.washesApi.apiV1WashesWashIdNotesPut({
                      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                      washId: wash.id || "",
                      generalNotesInputDto: { notes },
                    });
                    onUpdate();
                  }}
                />
              }
            >
              {wash.notes}
            </InfoTile>
          </Box>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.Wash}
          entityId={wash.id}
          entity={wash}
          entityUpdatePermission={AppPermission.WashManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={onUpdate}
        />

        {/* Stages */}
        <ViewContentBlock>
          <Typography variant='h2'>Stages</Typography>
          <GeneralStageHistory
            stageEnumType='WashStage'
            stageHistory={wash.stageHistory}
            withUpdateButtons={hasPermissions([AppPermission.WashManage])}
            onUpdateStage={async (newStage) => {
              try {
                const response = await apiClient.washesApi.apiV1WashesWashIdStagePut({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  washId: wash.id || "",
                  generalStageUpdateDtoOfWashStage: {
                    newStage: newStage,
                  },
                });
                onUpdate(response.data);
              } catch (err) {
                const validation2 = ValidationHelper.handleApiErrorResponse(err);
                enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
              }
            }}
          />
        </ViewContentBlock>

        {!ArrayHelper.isEmpty(wash.attachments) && (
          <ViewContentBlock>
            <FileListView
              files={FileItem.createManyFrom(wash.attachments)}
              maxVisibleFiles={10}
              itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
              itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
            />
          </ViewContentBlock>
        )}

        {/* Summary */}
        <ViewContentBlock direction='row' alignSelf='end' sx={{ width: "fit-content" }}>
          <GeneralPriceSummaryDisplay
            sx={{ minWidth: { xxs: "100%", md: "300px" } }}
            summary={{
              currency: wash.currency,
              subTotal: wash.subTotal,
              subTotalIncDiscount: wash.subTotalIncDiscount,
              discount: wash.discount,
              tax: wash.tax,
              total: wash.total,
            }}
          />
        </ViewContentBlock>
      </ViewLayout>
    </PageTabContent>
  );
}
